import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { salaryGradesApi } from '../plantillaApi';
import { SALARY_TYPE, type SalaryGradeListItem, type SalaryTypeValue } from '../plantillaTypes';

/**
 * Create or edit one rate in the salary schedule.
 *
 * The four key fields are all required and all constrained, because together they identify
 * the rate: the same grade and step is a different amount in each tranche, and the two
 * salary types pay differently for the same number.
 */
export function SalaryGradeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: SalaryGradeListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [salaryType, setSalaryType] = useState<string | null>(String(editing?.type ?? SALARY_TYPE.general));
  const [grade, setGrade] = useState(String(editing?.grade ?? ''));
  const [step, setStep] = useState(String(editing?.step ?? 1));
  const [tranche, setTranche] = useState(String(editing?.tranche ?? 1));
  const [effectivityDate, setEffectivityDate] = useState(editing?.effectivityDate?.slice(0, 10) ?? '');
  const [monthlyAmount, setMonthlyAmount] = useState(String(editing?.monthlyAmount ?? ''));
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const whole = (value: string, min: number, max: number) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= min && parsed <= max;
  };

  const gradeValid = whole(grade, 1, 33);
  const stepValid = whole(step, 1, 8);
  const trancheValid = whole(tranche, 1, 20);
  const amount = Number(monthlyAmount);
  const amountValid = Number.isFinite(amount) && amount >= 0 && amount <= 10_000_000;
  const dateValid = effectivityDate.length > 0;

  const valid = gradeValid && stepValid && trancheValid && amountValid && dateValid && salaryType !== null;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave || salaryType === null) return;

    setSaving(true);
    setError(null);

    const request = {
      type: Number(salaryType) as SalaryTypeValue,
      grade: Number(grade),
      step: Number(step),
      tranche: Number(tranche),
      effectivityDate,
      monthlyAmount: amount,
      description: description.trim() || null,
    };

    try {
      if (editing) await salaryGradesApi.update(editing.id, request);
      else await salaryGradesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the salary grade.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, { idPrefix: 'salary-grade' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit salary grade' : 'New salary grade'}
      size="md"
      id="salary-grade-modal"
      /*
        Every value sits inside the range the form already enforces — grade 1–33, step 1–8,
        tranche 1–20 — so a click produces a row the server accepts rather than one it refuses.
        The amount is a plausible monthly rate; the schedule is public and names nobody.
      */
      autogenerate={{
        onGenerate: () => {
          setGrade(String(1 + Math.floor(Math.random() * 33)));
          setStep(String(1 + Math.floor(Math.random() * 8)));
          setTranche(String(1 + Math.floor(Math.random() * 20)));
          setEffectivityDate(todayCalendarDate());
          setMonthlyAmount(String(20000 + Math.floor(Math.random() * 60000)));
          setDescription('Sample rate.');
        },
        disabled: saving,
      }}
      confirm={{
        id: 'salary-grade-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="salary-grade-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="salary-grade-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="salary-grade-type"
          label="Type"
          required
          clearable={false}
          description="Which salary type this rate belongs to. The same grade pays differently on each."
          value={salaryType}
          onChange={setSalaryType}
          options={[
            { value: String(SALARY_TYPE.general), label: 'General' },
            { value: String(SALARY_TYPE.health), label: 'Health' },
          ]}
          disabled={saving}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            id="salary-grade-grade"
            label="Grade"
            required
            type="number"
            min={1}
            max={33}
            description="The grade number, 1 to 33."
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            error={grade === '' || gradeValid ? undefined : 'Enter a grade between 1 and 33.'}
            disabled={saving}
          />

          <TextField
            id="salary-grade-step"
            label="Step"
            required
            type="number"
            min={1}
            max={8}
            description="The step within the grade. Eight steps to a grade."
            value={step}
            onChange={(e) => setStep(e.target.value)}
            error={stepValid ? undefined : 'Enter a step between 1 and 8.'}
            disabled={saving}
          />

          <TextField
            id="salary-grade-tranche"
            label="Tranche"
            required
            type="number"
            min={1}
            max={20}
            description="Which tranche this rate belongs to. The same grade and step is a different amount in each."
            value={tranche}
            onChange={(e) => setTranche(e.target.value)}
            error={trancheValid ? undefined : 'Enter a tranche between 1 and 20.'}
            disabled={saving}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            id="salary-grade-effectivity-date"
            label="Effective from"
            required
            type="date"
            description="The day this rate takes effect. A rate is only meaningful from a date."
            value={effectivityDate}
            onChange={(e) => setEffectivityDate(e.target.value)}
            error={dateValid ? undefined : 'Choose the date this rate takes effect.'}
            disabled={saving}
          />

          <TextField
            id="salary-grade-monthly-amount"
            label="Monthly rate"
            required
            type="number"
            min={0}
            step="0.01"
            description="The monthly rate for this grade, step and tranche."
            value={monthlyAmount}
            onChange={(e) => setMonthlyAmount(e.target.value)}
            error={monthlyAmount === '' || amountValid ? undefined : 'Enter an amount up to 10,000,000.'}
            disabled={saving}
          />
        </div>

        <TextField
          id="salary-grade-description"
          label="Description"
          description="Optional note, e.g. the band of titles that usually sit at this grade."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
